package dev.terrakok.mapitan.domain

import androidx.compose.ui.graphics.Color
import org.maplibre.spatialk.geojson.Point

sealed interface Operation {
    val id: String
    val time: Long
    fun reverse(id: String, time: Long): Operation

    data class AddPin(
        override val id: String,
        override val time: Long,
        val pin: Pin
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = RemovePin(id, time, pin)
    }

    data class RemovePin(
        override val id: String,
        override val time: Long,
        val pin: Pin
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = AddPin(id, time, pin)
    }

    data class ColorPin(
        override val id: String,
        override val time: Long,
        val pinId: String,
        val oldColor: Color,
        val newColor: Color
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            ColorPin(id, time, pinId, newColor, oldColor)
    }

    data class MovePin(
        override val id: String,
        override val time: Long,
        val pinId: String,
        val oldPoint: Point,
        val newPoint: Point,
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            MovePin(id, time, pinId, newPoint, oldPoint)
    }

    data class AddLabel(
        override val id: String,
        override val time: Long,
        val label: Label
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = RemoveLabel(id, time, label)
    }

    data class RemoveLabel(
        override val id: String,
        override val time: Long,
        val label: Label
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = AddLabel(id, time, label)
    }

    data class ColorLabel(
        override val id: String,
        override val time: Long,
        val labelId: String,
        val oldColor: Color,
        val newColor: Color
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            ColorLabel(id, time, labelId, newColor, oldColor)
    }

    data class NewLabelText(
        override val id: String,
        override val time: Long,
        val labelId: String,
        val oldText: String,
        val newText: String
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            NewLabelText(id, time, labelId, newText, oldText)
    }

    data class MoveLabel(
        override val id: String,
        override val time: Long,
        val labelId: String,
        val oldPoint: Point,
        val newPoint: Point,
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            MoveLabel(id, time, labelId, newPoint, oldPoint)
    }

    data class AddTrack(
        override val id: String,
        override val time: Long,
        val track: Track
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = RemoveTrack(id, time, track)
    }

    data class RemoveTrack(
        override val id: String,
        override val time: Long,
        val track: Track
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = AddTrack(id, time, track)
    }

    data class ColorTrack(
        override val id: String,
        override val time: Long,
        val trackId: String,
        val oldColor: Color,
        val newColor: Color
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            ColorTrack(id, time, trackId, newColor, oldColor)
    }

    data class NewTrackLineThickness(
        override val id: String,
        override val time: Long,
        val trackId: String,
        val oldThickness: Int,
        val newThickness: Int
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            NewTrackLineThickness(id, time, trackId, newThickness, oldThickness)
    }

    data class NewTrackLineStyle(
        override val id: String,
        override val time: Long,
        val trackId: String,
        val oldStyle: LineStyle,
        val newStyle: LineStyle
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            NewTrackLineStyle(id, time, trackId, newStyle, oldStyle)
    }

    data class AddTrackPoint(
        override val id: String,
        override val time: Long,
        val trackId: String,
        val index: Int,
        val point: TrackPoint
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = RemoveTrackPoint(id, time, trackId, index, point)
    }

    data class RemoveTrackPoint(
        override val id: String,
        override val time: Long,
        val trackId: String,
        val index: Int,
        val point: TrackPoint
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = AddTrackPoint(id, time, trackId, index, point)
    }

    data class MoveTrackPoint(
        override val id: String,
        override val time: Long,
        val trackId: String,
        val pointId: String,
        val oldPoint: Point,
        val newPoint: Point,
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            MoveTrackPoint(id, time, trackId, pointId, newPoint, oldPoint)
    }

    data class AddArea(
        override val id: String,
        override val time: Long,
        val area: Area
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = RemoveArea(id, time, area)
    }

    data class RemoveArea(
        override val id: String,
        override val time: Long,
        val area: Area
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = AddArea(id, time, area)
    }

    data class ColorArea(
        override val id: String,
        override val time: Long,
        val areaId: String,
        val oldColor: Color,
        val newColor: Color
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            ColorArea(id, time, areaId, newColor, oldColor)
    }

    data class NewAreaLineThickness(
        override val id: String,
        override val time: Long,
        val areaId: String,
        val oldThickness: Int,
        val newThickness: Int
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            NewAreaLineThickness(id, time, areaId, newThickness, oldThickness)
    }

    data class NewAreaLineStyle(
        override val id: String,
        override val time: Long,
        val areaId: String,
        val oldStyle: LineStyle,
        val newStyle: LineStyle
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            NewAreaLineStyle(id, time, areaId, newStyle, oldStyle)
    }

    data class AddAreaPoint(
        override val id: String,
        override val time: Long,
        val areaId: String,
        val index: Int,
        val point: TrackPoint
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = RemoveAreaPoint(id, time, areaId, index, point)
    }

    data class RemoveAreaPoint(
        override val id: String,
        override val time: Long,
        val areaId: String,
        val index: Int,
        val point: TrackPoint
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation = AddAreaPoint(id, time, areaId, index, point)
    }

    data class MoveAreaPoint(
        override val id: String,
        override val time: Long,
        val areaId: String,
        val pointId: String,
        val oldPoint: Point,
        val newPoint: Point,
    ) : Operation {
        override fun reverse(id: String, time: Long): Operation =
            MoveAreaPoint(id, time, areaId, pointId, newPoint, oldPoint)
    }
}